export const BOOT_BACKGROUND_URL = '/background/login_background.webp';

const BOOT_CACHE_NAME = 'skychat-boot-assets-v1';
const BOOT_DB_NAME = 'skychat-boot-assets';
const BOOT_DB_VERSION = 1;
const BOOT_DB_STORE = 'assets';
let memoryBackgroundUrl = '';
let backgroundLoadPromise: Promise<string> | null = null;

function blobToObjectUrl(blob: Blob): string {
  if (!blob.size) throw new Error('Boot background is empty');
  return URL.createObjectURL(blob);
}

function openBootDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(BOOT_DB_NAME, BOOT_DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(BOOT_DB_STORE)) {
        database.createObjectStore(BOOT_DB_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readFromIndexedDb(): Promise<Blob | null> {
  const database = await openBootDatabase();
  if (!database) return null;

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(BOOT_DB_STORE, 'readonly');
    const request = transaction.objectStore(BOOT_DB_STORE).get(BOOT_BACKGROUND_URL);
    request.onsuccess = () => resolve(request.result instanceof Blob ? request.result : null);
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => database.close();
    transaction.onerror = () => database.close();
  });
}

async function writeToIndexedDb(blob: Blob): Promise<void> {
  const database = await openBootDatabase();
  if (!database) return;

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(BOOT_DB_STORE, 'readwrite');
    transaction.objectStore(BOOT_DB_STORE).put(blob, BOOT_BACKGROUND_URL);
    transaction.oncomplete = () => {
      database.close();
      resolve();
    };
    transaction.onerror = () => {
      database.close();
      reject(transaction.error);
    };
  });
}

async function readFromCacheStorage(): Promise<Blob | null> {
  if (typeof caches === 'undefined') return null;
  const cache = await caches.open(BOOT_CACHE_NAME);
  const response = await cache.match(BOOT_BACKGROUND_URL);
  return response ? response.blob() : null;
}

async function persistBackground(blob: Blob): Promise<void> {
  const writes: Promise<unknown>[] = [writeToIndexedDb(blob)];
  if (typeof caches !== 'undefined') {
    writes.push(
      caches.open(BOOT_CACHE_NAME).then((cache) =>
        cache.put(
          BOOT_BACKGROUND_URL,
          new Response(blob, { headers: { 'Content-Type': blob.type || 'image/webp' } })
        )
      )
    );
  }
  await Promise.allSettled(writes);
}

async function loadFromLocalCache(): Promise<string> {
  const cacheStorageBlob = await readFromCacheStorage().catch(() => null);
  if (cacheStorageBlob?.size) return blobToObjectUrl(cacheStorageBlob);

  const indexedDbBlob = await readFromIndexedDb().catch(() => null);
  if (indexedDbBlob?.size) return blobToObjectUrl(indexedDbBlob);

  const response = await fetch(BOOT_BACKGROUND_URL, { cache: 'force-cache' });
  if (!response.ok) throw new Error(`Boot background download failed (${response.status})`);
  const blob = await response.blob();
  if (!blob.size) throw new Error('Boot background is empty');
  await persistBackground(blob);
  return blobToObjectUrl(blob);
}

export function getBootBackgroundUrl(): Promise<string> {
  if (memoryBackgroundUrl) return Promise.resolve(memoryBackgroundUrl);
  if (backgroundLoadPromise) return backgroundLoadPromise;

  backgroundLoadPromise = loadFromLocalCache()
    .catch(() => BOOT_BACKGROUND_URL)
    .then((url) => {
      memoryBackgroundUrl = url;
      return url;
    });
  return backgroundLoadPromise;
}
